<template>
  <div class="edit-row">
    <div data-testid="shrinkage-edit-from">
      <label for="From">From</label>
      <AlDatePicker
        :modelValue="month"
        v-model="startDate"
        name="From"
        :min="minDate"
        :max="endDate || maxDate"
      />
    </div>

    <div data-testid="shrinkage-edit-to">
      <label for="To">To</label>
      <AlDatePicker
        :modelValue="maxDate"
        v-model="endDate"
        name="To"
        :min="startDate || minDate"
        :max="maxDate"
      />
    </div>
    <div data-testid="shrinkage-edit-percentage">
      <label>Percentage</label>
      <Decimal
        v-model="percentage"
        append="%"
        :min="0"
        :max="100"
        :step="0.1"
        :decimals="1"
      />
    </div>

    <div data-testid="shrinkage-edit-teams">
      <label>Teams</label>
      <AlSelect
        v-model="teams"
        :options="teamOptions"
        track-by="id"
        placeholder="All Teams"
        :multiple="true"
        :close-on-select="false"
      ></AlSelect>
    </div>

    <div data-testid="shrinkage-edit-categories">
      <label>Categories</label>
      <AlSelect
        v-model="categories"
        :options="categoryOptions"
        track-by="id"
        placeholder="All Shrinkage"
        :multiple="true"
        :close-on-select="false"
      ></AlSelect>
    </div>
    <div class="edit-actions">
      <AlButton
        @click="save"
        data-testid="save-row"
        id="shrinkage-edit-save-row"
        ><i class="far fa-check" @click="save"
      /></AlButton>
      <AlButton
        @click="cancel"
        variant="secondary"
        data-testid="delete-row"
        id="shrinkage-edit-cancel-row"
        ><i class="far fa-times"
      /></AlButton>
    </div>
  </div>
</template>

<script>
import {
  AlDatePicker,
  Al2Button as AlButton,
  AlSelect,
} from "@playvox/almendra-components";
import { Decimal } from "@agyletime/wfm-vue-common";
import {
  format,
  endOfDay,
  startOfDay,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

export default {
  name: "EditShrinkageRow",
  components: {
    AlDatePicker,
    AlButton,
    Decimal,
    AlSelect,
  },
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
    shrinkage: {
      type: Object,
      required: true,
    },
    teamOptions: {
      type: Array,
      required: true,
    },
    categoryOptions: {
      type: Array,
      required: true,
    },
    month: {
      type: String,
      required: true,
    },
  },
  data() {
    let startDate = utcToZonedTime(this.month);
    let endDate = endOfMonth(utcToZonedTime(this.month));

    if (this.shrinkage?.startDate) {
      startDate = utcToZonedTime(this.shrinkage.startDate, "UTC");
    }

    if (this.shrinkage?.endDate) {
      endDate = utcToZonedTime(this.shrinkage.endDate, "UTC");
    }
    return {
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
      percentage: this.shrinkage?.percentage || 0,
      teams: this.shrinkage?.teams || [],
      categories: this.shrinkage?.categories || [],
    };
  },
  methods: {
    cancel() {
      this.$emit("cancel");
    },
    save() {
      //We're storing the dates selected in UTC
      //When the aggregation runs it will convert these dates into the plans timezone
      //This was done so if the plans timezone was edited the adjustments would still fall on the first hour of the date selected
      //We need to find the start of day in the UTC timezone then convert it back to the browsers timezone
      // e.g if the browser is in GMT-10 (Hawaii) and the user selects 2020-01-01 as a start and/or end date
      //      new Date("2020-01-01")    --> 2019-12-31 14:00:00                         The browser will convert the string "2020-01-01" from UTC to GMT-10
      //  --> utcToZonedTime(...,"UTC") --> 2020-01-01 00:00:00                         We convert it back to this zone so we get midnight of that day
      //  --> startOfDay()/endOfDay()   --> 2020-01-01 00:00:00 / 2020-01-01 23:59:59   Find the start/end of day in this timezone
      //  --> zonedTimeToUtc(...,"UTC") --> 2019-12-31 14:00:00 / 2020-01-01 13:59:59   Then convert it back to UTC so we can post it to the API
      const result = {
        startDate: zonedTimeToUtc(
          startOfDay(utcToZonedTime(new Date(this.startDate), "UTC")),
          "UTC"
        ),
        endDate: zonedTimeToUtc(
          endOfDay(utcToZonedTime(new Date(this.endDate), "UTC")),
          "UTC"
        ),
        percentage: this.percentage,
        teams: this.teams,
        categories: this.categories,
      };
      this.$emit("save", result);
    },
  },
  computed: {
    minDate() {
      const date = utcToZonedTime(this.month);
      return format(startOfMonth(date), "yyyy-MM-dd");
    },
    maxDate() {
      const date = utcToZonedTime(this.month);
      return format(endOfMonth(date), "yyyy-MM-dd");
    },
    defaultDate() {
      const date = utcToZonedTime(this.month);
      return format(date, "yyyy-MM-dd");
    },
  },
};
</script>
